import React from 'react';
import styles from './Mensagem.module.css';
import Badge from '../../components/layout/Badge';
import { ChatDtoEstado } from '@tera/shared/src/dto/ChatDto';
import Icon from '@/components/Icon';
import {
  mdiAccount,
  mdiAlertCircle,
  mdiCheck,
  mdiCheckAll,
  mdiCheckCircle,
  mdiClipboardCheckOutline,
  mdiCloseCircleOutline,
  mdiContentCopy,
  mdiDownload,
  mdiEmail,
  mdiEye,
  mdiHeadset,
  mdiLink,
  mdiPhone,
  mdiRobot,
  mdiSchool,
  mdiTag,
  mdiToolboxOutline,
  mdiThumbDown,
  mdiThumbUp,
} from '@mdi/js';
import { Acao, AcaoTipo, acaoHref, acaoLabel } from '@tera/shared/src/fluxoConfig/acoes';
import {
  ChatMensagemDto,
  ChatMensagemDtoEstado,
} from '@tera/shared/src/dto/ChatMensagemDto';
import TextoData from '../../components/tipografia/TextoData';
import { Conteudo } from './conteudo/Conteudo';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import Marcador from '@/components/layout/Marcador';
import VBox from '@/components/layout/VBox';

type IconDescriptor = {
  icon: string;
  label: string;
};

const iconePorTipoAcao: Record<AcaoTipo, string> = {
  link: mdiLink,
  telefone: mdiPhone,
  email: mdiEmail,
  copiar: mdiContentCopy,
};

// Botão de ação (CTA externa). `link`/`telefone`/`email` viram âncora navegável;
// `copiar` copia o conteúdo para a área de transferência (útil p/ cupom/PIX/2FA).
// A ação só é interativa na visão do cliente (webchat): links como o do mini
// formulário são de uso único e endereçados a ele. Para o atendente a ação é
// apenas o registro do que foi enviado — vira um rótulo inerte, sem href.
const AcaoBotao: React.FC<{ acao: Acao; desabilitada?: boolean }> = ({ acao, desabilitada }) => {
  const [copiado, setCopiado] = React.useState(false);
  const href = acaoHref(acao);
  const label = acaoLabel(acao);
  const icone = iconePorTipoAcao[acao.tipo] ?? mdiLink;

  const conteudo = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', width: '100%', justifyContent: 'center' }}>
      <Icon path={copiado ? mdiCheck : icone} size={0.8} />
      <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
        {copiado ? 'Copiado!' : label}
      </span>
    </span>
  );

  const estiloBase: React.CSSProperties = {
    width: '100%',
    margin: '4px 0',
    padding: '8px 12px',
    borderRadius: 'var(--tc-border-radius, 6px)',
    border: '1px solid var(--tc-color-primary, #5C68E2)',
    color: 'var(--tc-color-primary, #5C68E2)',
    background: 'transparent',
    textDecoration: 'none',
    display: 'block',
    textAlign: 'center',
    cursor: 'pointer',
    font: 'inherit',
  };

  if (desabilitada) {
    return (
      <div
        style={{
          ...estiloBase,
          border: '1px solid var(--tc-color-gray-300)',
          color: 'var(--tc-color-gray-500)',
          cursor: 'default',
        }}
        title="Ação enviada ao cliente"
      >
        {conteudo}
      </div>
    );
  }

  if (acao.tipo === 'copiar') {
    return (
      <button
        type="button"
        style={estiloBase}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(acao.conteudo);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 1500);
          } catch {
            /* clipboard indisponível — ignora silenciosamente */
          }
        }}
      >
        {conteudo}
      </button>
    );
  }

  return (
    <a href={href ?? '#'} target="_blank" rel="noopener noreferrer" style={estiloBase}>
      {conteudo}
    </a>
  );
};

/** Um campo do snapshot gravado por `formulario-publico` ao concluir o formulário. */
type RespostaFormulario = {
  label: string;
  valor: string;
  arquivo?: { id: number; resposta_id: number; mime_type?: string };
};

// Tipos que o navegador exibe (a rota devolve `inline`); os demais baixam.
const MIMES_VISUALIZAVEIS = /^(image\/|video\/|audio\/|text\/plain$|application\/pdf$)/;

const ValorResposta: React.FC<{ resposta: RespostaFormulario }> = ({ resposta }) => {
  const estilo: React.CSSProperties = {
    fontWeight: 500,
    textAlign: 'end',
    wordBreak: 'break-word',
  };

  if (!resposta.arquivo) {
    return (
      <span className="rr-mask" style={estilo}>
        {resposta.valor}
      </span>
    );
  }

  const { id, resposta_id, mime_type } = resposta.arquivo;
  const visualizavel = MIMES_VISUALIZAVEIS.test(mime_type ?? '');

  return (
    <a
      className="rr-mask"
      href={`/api/mini-formulario/resposta/${resposta_id}/arquivo/${id}`}
      target="_blank"
      rel="noopener noreferrer"
      title={visualizavel ? 'Visualizar arquivo' : 'Baixar arquivo'}
      style={{ ...estilo, color: 'var(--tc-color-primary)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
    >
      <Icon path={visualizavel ? mdiEye : mdiDownload} size={0.7} />
      {resposta.valor}
    </a>
  );
};

const iconDescriptor: Record<ChatMensagemDtoEstado, IconDescriptor> = {
  em_processamento: { icon: mdiCheck, label: 'Enviando mensagem' },
  processada: { icon: mdiCheckAll, label: 'Mensagem entregue' },
  lida: { icon: mdiCheckAll, label: 'Mensagem lida' },
  erro: { icon: mdiAlertCircle, label: 'Erro ao enviar mensagem' },
};

const badgeByStatus: Record<
  ChatDtoEstado,
  (responsavel: string, solucionado?: boolean) => JSX.Element
> = {
  chatbot: r => (
    <Badge variant="info">
      {' '}
      <Icon path={mdiRobot} size={1} /> Chatbot
    </Badge>
  ),
  atendimento: r => (
    <Badge variant="warning">
      <Icon path={mdiHeadset} size={1} /> {r ?? `Em atendimento`}
    </Badge>
  ),
  finalizado: (r, solucionado) => (
    <Badge variant="primary">
      <Icon path={mdiCheckCircle} size={1} /> Finalizado{' '}
      {r ? `por ${r}` : null}
      {solucionado === true && (
        <Icon path={mdiThumbUp} size={1} color="var(--tc-color-success)" />
      )}
      {solucionado === false && (
        <Icon
          path={mdiThumbDown}
          size={1}
          color="var(--tc-color-gray-500)"
        />
      )}
    </Badge>
  ),
  aguardando_cliente: r => (
    <Badge variant="warning">
      <Icon path={mdiAccount} size={1} /> (Aguardando cliente)
    </Badge>
  ),
  aguardando_atendente: r => (
    <Badge variant="warning">
      <Icon path={mdiHeadset} size={1} /> (Aguardando atendente)
    </Badge>
  ),
};

type BadgeStatusProps = {
  status: ChatDtoEstado;
  responsavel: string;
  solucionado?: boolean;
};

const BadgeStatus = ({
  status,
  responsavel,
  solucionado,
}: BadgeStatusProps): JSX.Element | null => {
  const bFunc = badgeByStatus[status];
  if (bFunc) return bFunc(responsavel, solucionado);
  return null;
};

const AutorMensagem = ({
  mensagens,
  index,
  origem,
  autor,
  espelhar,
}: {
  mensagens: ChatMensagemDto[];
  index: number;
  origem: string;
  autor: string;
  espelhar?: boolean;
}) => {
  if (index > mensagens.length - 1) return null;

  // Na perspectiva do cliente (webchat), o próprio cliente fica à direita e todo o
  // resto à esquerda — o inverso do alinhamento padrão dos nomes.
  const alinhamento: React.CSSProperties | undefined = espelhar
    ? { textAlign: origem === 'usuario' ? 'right' : 'left' }
    : undefined;

  if (origem === 'sistema')
    return <div className={styles.mensagemSistema}>Sistema</div>;

  if (
    origem === 'bot' &&
    (index === mensagens.length - 1 || mensagens[index + 1].origem !== 'bot')
  ) {
    return <div className={styles.mensagemNomeBot} style={alinhamento}>{autor}</div>;
  }

  if (
    origem === 'interna' &&
    (index === mensagens.length - 1 || mensagens[index + 1].origem !== 'bot')
  ) {
    return (
      <div className={styles.mensagemInterna}>
        Mensagem interna (só para atendentes)
      </div>
    );
  }
  if (
    origem === 'usuario' &&
    (index === mensagens.length - 1 ||
      mensagens[index + 1].origem !== 'usuario')
  )
    return <div className={[styles.mensagemNomeUsuario, 'rr-mask'].join(' ')} style={alinhamento}>{autor}</div>;

  if (origem === 'atendente') {
    const rAnt = mensagens[index + 1]?.metadados?.remetente?.nome
    const remetente = mensagens[index]?.metadados?.remetente?.nome;

    if (
      index === mensagens.length - 1 ||
      mensagens[index + 1].origem !== 'atendente' ||
      rAnt !== remetente
    )
      return (
        <div className={styles.mensagemNomeAtendente} style={alinhamento}>
          {remetente ?? 'Atendente'}
        </div>
      );
  }

  return null;
};

type MensagemProps = {
  mensagens: ChatMensagemDto[];
  item: ChatMensagemDto;
  index: number;
  autor?: string;
  /** Perspectiva do cliente (webchat): as mensagens do próprio cliente vão para a direita. */
  espelhar?: boolean;
  onOptionChosen?: (opcao: string) => void;
  onSelectedKbSection: (section: any) => void;
  onBlockContact?: () => void;
  onAudioPlaying?: (isPlaying: boolean) => void;
};

const Mensagem = ({
  item,
  index,
  mensagens: value,
  autor = 'Cliente',
  espelhar,
  onOptionChosen,
  onSelectedKbSection,
  onBlockContact,
  onAudioPlaying,
}: MensagemProps) => {
  // Oculta o comando interno "/start" (dispara o fluxo) tanto para o cliente quanto
  // para o atendente — é ruído, não conteúdo de conversa.
  if (
    item.origem === 'usuario' &&
    (item.conteudo_ou_url ?? '').trim().toLowerCase() === '/start'
  ) {
    return null;
  }

  const data = item.data_criacao
    ? new Date(item.data_criacao).setHours(0, 0, 0, 0)
    : '';
  const dataAnt =
    index < value.length - 1 && value[index + 1].data_criacao
      ? new Date(value[index + 1].data_criacao!).setHours(0, 0, 0, 0)
      : '';

  // Resultado de um mini formulário concluído pelo cliente. Usa a caixa das
  // mensagens internas (mesmo balão do "whisper"): é conteúdo de bastidor, que
  // interessa ao atendente e não foi dito na conversa — por isso fica fora da
  // visão do cliente (webchat), que acabou de preencher esses mesmos valores.
  if (
    item.origem === 'sistema' &&
    item.conteudo_ou_url === 'CHAT_MINI_FORMULARIO'
  ) {
    if (espelhar) return null;

    const respostas = (item.metadados?.respostas ?? []) as RespostaFormulario[];
    const nomeFormulario = item.metadados?.mini_formulario_nome;

    return (
      <div
        className={[
          styles.mensagemContainer,
          styles['mensagemContainer-interna'],
        ].join(' ')}
        key={`mensagem-${index}`}
      >
        <div className={styles.caixaMensagem}>
          <div className={styles.mensagemInterna}>
            Formulário preenchido
          </div>
          <div
            className={[styles.mensagem, styles['mensagem-interna']].join(' ')}
          >
            <div className={styles.mensagemConteudo}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 600,
                  marginBottom: '4px',
                }}
              >
                <Icon path={mdiClipboardCheckOutline} size={0.8} />
                <span>{nomeFormulario ?? 'Mini formulário'}</span>
              </div>
              <VBox gap="2px">
                {respostas.map((resposta, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      gap: '12px',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span style={{ color: 'var(--tc-color-gray-500)' }}>
                      {resposta.label}
                    </span>
                    <ValorResposta resposta={resposta} />
                  </div>
                ))}
              </VBox>
            </div>
            <div
              className={styles.mensagemData}
              style={{ width: '100%', textAlign: 'center' }}
            >
              <TextoData value={item.data_criacao} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Abertura e desistência do mini formulário: bastidor útil ao atendente
  // ("viu o formulário e não enviou", "desistiu") e sem sentido na visão do
  // cliente (webchat), que acabou de fazer a própria ação. As tools do agente
  // entram na mesma regra: o cliente, quando a consulta demora, já recebe o
  // aviso pelo canal.
  if (
    item.origem === 'sistema' &&
    (item.conteudo_ou_url === 'CHAT_MINI_FORMULARIO_ABERTO' ||
      item.conteudo_ou_url === 'CHAT_MINI_FORMULARIO_CANCELADO' ||
      item.conteudo_ou_url === 'AI_TOOL') &&
    espelhar
  ) {
    return null;
  }

  if (item.origem === 'sistema') {
    return (
      <div className={styles.mensagemContainer} key={`mensagem-${index}`}>
        <div className={styles.mensagemSistema}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: '2px',
              width: '100%',
              lineHeight: '24px',
              margin: '0 12px',
            }}
          >
            {item.conteudo_ou_url === 'CHAT_MARCADOR' &&
              item.metadados?.marcador === 'TRANSFERIDO' && (
                <>
                  <span></span>
                  <Badge variant="info">
                    Transferido para{' '}
                    {item.metadados.usuario_responsavel_nome ??
                      item.metadados.grupo_responsavel_nome}
                  </Badge>
                  <span>
                    <span>
                      <TextoData value={item.data_criacao} />
                    </span>
                  </span>
                </>
              )}
            {item.conteudo_ou_url === 'CHAT_MARCADOR' &&
              item.metadados?.marcador !== 'TRANSFERIDO' && (
                <>
                  <span></span>
                  {item.metadados?.marcador_id > 0 ? (
                    <Marcador color={item.metadados?.marcador_cor}>
                      Marcado como {item.metadados?.marcador}
                      {item.metadados?.usuario_nome != null && (
                        <span> por {item.metadados?.usuario_nome}</span>
                      )}
                    </Marcador>
                  ) : (
                    <Badge variant="info">
                      <Icon path={mdiTag} size={1} /> Marcado como{' '}
                      {item.metadados?.marcador}
                    </Badge>
                  )}
                  <span>
                    <span>
                      <TextoData value={item.data_criacao} />
                    </span>
                  </span>
                </>
              )}
            {item.conteudo_ou_url === 'CHAT_STATUS' && (
              <>
                <span></span>
                <BadgeStatus
                  status={item.metadados?.status as ChatDtoEstado}
                  responsavel={
                    item.metadados?.usuario_responsavel_nome ??
                    item.metadados?.grupo_responsavel_nome
                  }
                  solucionado={item.metadados?.solucionado}
                />
                <span>
                  <span>
                    <TextoData value={item.data_criacao} />
                  </span>
                </span>
              </>
            )}

            {item.conteudo_ou_url === 'CHAT_MINI_FORMULARIO_ABERTO' && (
              <>
                <span></span>
                <Badge variant="info">
                  <Icon path={mdiEye} size={1} /> Formulário aberto pelo cliente
                  {item.metadados?.mini_formulario_nome != null && (
                    <span>: {item.metadados.mini_formulario_nome}</span>
                  )}
                </Badge>
                <span>
                  <span>
                    <TextoData value={item.data_criacao} />
                  </span>
                </span>
              </>
            )}

            {item.conteudo_ou_url === 'CHAT_MINI_FORMULARIO_CANCELADO' && (
              <>
                <span></span>
                <Badge variant="warning">
                  <Icon path={mdiCloseCircleOutline} size={1} /> Formulário
                  cancelado pelo cliente
                  {item.metadados?.mini_formulario_nome != null && (
                    <span>: {item.metadados.mini_formulario_nome}</span>
                  )}
                  {item.metadados?.cancelar_label != null && (
                    <span> ("{item.metadados.cancelar_label}")</span>
                  )}
                </Badge>
                <span>
                  <span>
                    <TextoData value={item.data_criacao} />
                  </span>
                </span>
              </>
            )}

            {item.conteudo_ou_url === 'AI_TOOL' && (
              <>
                <span></span>
                <Badge variant="info">
                  <Icon path={mdiToolboxOutline} size={1} />
                  {item.metadados?.rotulo ?? 'Consultando'}
                  {item.metadados?.erro === true && (
                    <span> — falhou</span>
                  )}
                </Badge>
                <span>
                  <span>
                    <TextoData value={item.data_criacao} />
                  </span>
                </span>
              </>
            )}

            {item.conteudo_ou_url === 'CHAT_SOLUCIONADO' && (
              <>
                <span></span>
                <Badge variant='info'>
                  {item.metadados?.solucionado === true && <Icon path={mdiThumbUp} size={1} color='var(--tc-color-success)' />}
                  {item.metadados?.solucionado === false && <Icon path={mdiThumbDown} size={1} color='var(--tc-color-gray-500)' />}

                  Marcado como {item.metadados?.solucionado ? 'solucionado' : 'não resolvido'} por {item.metadados?.usuario_finalizador_nome}
                </Badge>
                <span>
                  <span>
                    <TextoData value={item.data_criacao} />
                  </span>
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <div
        className={[
          styles.mensagemContainer,
          styles[`mensagemContainer-${item.origem}`],
        ].join(' ')}
        style={
          espelhar
            ? { justifyContent: item.origem === 'usuario' ? 'flex-end' : 'flex-start' }
            : undefined
        }
        key={`mensagem-${index}`}
      >
        <div className={styles.caixaMensagem}>
          <AutorMensagem
            mensagens={value}
            index={index}
            origem={item.origem}
            autor={autor}
            espelhar={espelhar}
          />
          <div
            className={[
              styles.mensagem,
              styles[`mensagem-${item.origem}`],
            ].join(' ')}
          >
            <div className={styles.mensagemConteudo}>
              <Conteudo mime={item.tipo_mime} value={item} onBlockContact={onBlockContact} onAudioPlaying={onAudioPlaying} />
              {(item.metadados?.opcoes ?? item.opcoes)?.map(
                (opcao: any, idx: number) => (
                  <Botao
                    disabled={!onOptionChosen}
                    variant={onOptionChosen ? 'primary' : 'outline-secondary'}
                    onClick={() => onOptionChosen?.(opcao.content)}
                    key={idx}
                    style={{ width: '100%', margin: '4px 0' }}
                  >
                    <div className='rr-mask' style={{ width: '100%', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{opcao?.content ? opcao.content?.toString() : ""}</div>
                  </Botao>
                ),
              )}
              {((item.metadados?.acoes ?? (item as any).acoes) as Acao[] | undefined)?.map(
                (acao, idx) => (
                  // `espelhar` = visão do cliente (webchat); fora dela quem lê é o
                  // atendente, para quem a ação é só registro do que foi enviado.
                  <AcaoBotao acao={acao} desabilitada={!espelhar} key={acao?.id ?? idx} />
                ),
              )}
            </div>
            <div className={styles.mensagemData}>
              <HBox>
                <div
                  style={{
                    lineHeight: '16px',
                    color: 'var(--tc-color-gray-500)',
                  }}
                >
                  <TextoData value={item.data_criacao} />
                </div>
                <div
                  className={styles[`mensagem-status-${item.origem}`]}
                  style={{ lineHeight: '16px', margin: 'auto' }}
                >
                  {iconDescriptor[item.estado_atual!] && (
                    <Icon
                      path={iconDescriptor[item.estado_atual!].icon}
                      size={1}
                      title={iconDescriptor[item.estado_atual!].label + (item.estado_atual == 'erro' ? `: ${item.descricao_estado_atual ?? 'Erro desconhecido'}` : '')}
                    />
                  )}
                  {!iconDescriptor[item.estado_atual!] && (
                    <Icon
                      path={mdiCheck}
                      size={1}
                      color="var(--tc-color-gray-400)"
                      title="Enviando mensagem..."
                    />
                  )}
                </div>
              </HBox>
            </div>
          </div>
        </div>
        {item.origem === 'usuario' && item.metadados?.kb && (
          <HBox
            style={{
              maxWidth: '50%',
              justifyContent: 'flex-end',
              border: '1px solid var(--tc-color-gray-300)',
              padding: '4px',
              borderRadius: '32px',
            }}
          >
            <div
              style={{
                borderRadius: '40px',
                minHeight: '80px',
                minWidth: '80px',
                backgroundColor: 'var(--tc-color-gray-200)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon
                path={mdiSchool}
                size={3}
                color="var(--tc-color-gray-400)"
              />
            </div>
            <VBox style={{ lineHeight: '18px', padding: '4px' }}>
              <div
                style={{
                  textAlign: 'center',
                  color: 'var(--tc-color-gray-700)',
                }}
              >
                Documentos relevantes encontrados!
              </div>
              {item.metadados?.kb?.sessoes?.[0] && (
                <Botao
                  variant="outline-primary"
                  onClick={() =>
                    onSelectedKbSection(item.metadados!.kb.sessoes[0])
                  }
                >
                  1: {item.metadados.kb.sessoes[0].secao_titulo}
                </Botao>
              )}
              {item.metadados?.kb?.sessoes?.[1] && (
                <Botao
                  variant="outline-primary"
                  onClick={() =>
                    onSelectedKbSection(item.metadados!.kb.sessoes[1])
                  }
                >
                  2: {item.metadados.kb.sessoes[1].secao_titulo}
                </Botao>
              )}
            </VBox>
          </HBox>
        )}
      </div>

      {data !== dataAnt && (
        <div className={styles.marcadorData}>
          <span>{new Date(data).toLocaleDateString()}</span>
        </div>
      )}
    </>
  );
};
export default Mensagem;
